// v2.2 Phase C: dev preview for Pearson-based career matching.
// URL: /report/18-plus/preview-pearson?seed=1&name=Elmira&lang=ru
//
// Same deterministic mock-user generator as /preview, BUT topClusters is
// produced by Pearson matcher (src/lib/career_matcher.ts) instead of random
// shuffle. Everything else (riasecScores, topValues, topStyles, etc.)
// identical to /preview given same seed → enables A/B side-by-side.
//
// Also supports ?profile=aladdin|esmiralda|all-I|all-R|flat|pure-R|pure-I|
// pure-A|pure-S|pure-E|pure-C — fixed sentinel profiles for testing.

import { createFileRoute, Link } from "@tanstack/react-router";
import Report13Page from "@/components/report/Report13Page";
import {
  enrichWithPearson,
  type Lang1822,
  type Result1822,
  type RiasecCode1822,
} from "@/lib/reports1822";

type Profile =
  | "aladdin"
  | "esmiralda"
  | "all-I"
  | "all-R"
  | "flat"
  | "pure-R"
  | "pure-I"
  | "pure-A"
  | "pure-S"
  | "pure-E"
  | "pure-C"
  | null;

type Mode = "combined" | "interest-only";

type PreviewSearch = {
  seed: number;
  name: string;
  age: number;
  lang: Lang1822;
  profile: Profile;
  /** v2.4: combined (default — interest+values) | interest-only (для A/B) */
  mode: Mode;
};

export const Route = createFileRoute("/report/18-plus/preview-pearson")({
  validateSearch: (search: Record<string, unknown>): PreviewSearch => ({
    seed:
      typeof search.seed === "number"
        ? search.seed
        : typeof search.seed === "string"
          ? parseInt(search.seed, 10) || 1
          : 1,
    name:
      typeof search.name === "string" && search.name ? search.name : "Elmira",
    age:
      typeof search.age === "number"
        ? search.age
        : typeof search.age === "string"
          ? parseInt(search.age, 10) || 22
          : 22,
    lang: (typeof search.lang === "string" && ["ru", "az", "en"].includes(search.lang)
      ? search.lang
      : "ru") as Lang1822,
    profile: (typeof search.profile === "string" &&
    [
      "aladdin",
      "esmiralda",
      "all-I",
      "all-R",
      "flat",
      "pure-R",
      "pure-I",
      "pure-A",
      "pure-S",
      "pure-E",
      "pure-C",
    ].includes(search.profile)
      ? search.profile
      : null) as Profile,
    mode:
      typeof search.mode === "string" && search.mode === "interest-only"
        ? "interest-only"
        : "combined",
  }),
  head: () => ({
    meta: [
      { title: "Yolutap · Отчёт 18+ — Pearson preview" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PreviewPearsonPage,
});

// Tiny deterministic RNG (mulberry32) — same seed → same report.
function rng(seed: number) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const RIASEC_CODES: RiasecCode1822[] = ["R", "I", "A", "S", "E", "C"];
const VALUE_KEYS = [
  "impact",
  "income",
  "autonomy",
  "stability",
  "prestige",
  "creativity",
  "growth",
  "variety",
  "social",
  "meaning",
  "structure",
  "leadership",
];
const STYLE_KEYS = [
  "solo",
  "team",
  "data",
  "people",
  "hands",
  "ideas",
  "outdoor",
  "office",
  "fast",
  "deep",
  "structured",
  "flexible",
];
const SKILL_KEYS = [
  "analytical",
  "communication",
  "creative",
  "physical",
  "technical",
  "leadership",
];
const DEPTH_KEYS = ["specialist", "generalist", "leader", "craftsman"];
const STATUS_KEYS = ["student", "employed", "seeking", "entrepreneur"];

function shuffle<T>(arr: T[], rand: () => number): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Sentinel profiles — те же RIASEC-вектора что в scripts/test-career-matcher.mjs.
// Шкала score1822 (raw 0-21), не O*NET (0-7).
const SENTINELS: Record<Exclude<Profile, null>, Record<RiasecCode1822, number>> = {
  aladdin: { R: 0.0, I: 1.5, A: 0.0, S: 0.0, E: 12.8, C: 7.2 },
  esmiralda: { R: 0.0, I: 2.25, A: 0.0, S: 15.75, E: 4.8, C: 0.0 },
  "all-I": { R: 0.0, I: 12.75, A: 6.6, S: 0.0, E: 0.0, C: 1.2 },
  "all-R": { R: 20.8, I: 1.5, A: 0.0, S: 0.0, E: 0.0, C: 7.2 },
  flat: { R: 4.0, I: 4.0, A: 4.0, S: 4.0, E: 4.0, C: 4.0 },
  "pure-R": { R: 6.5, I: 1.5, A: 1.5, S: 1.5, E: 1.5, C: 1.5 },
  "pure-I": { R: 1.5, I: 6.5, A: 1.5, S: 1.5, E: 1.5, C: 1.5 },
  "pure-A": { R: 1.5, I: 1.5, A: 6.5, S: 1.5, E: 1.5, C: 1.5 },
  "pure-S": { R: 1.5, I: 1.5, A: 1.5, S: 6.5, E: 1.5, C: 1.5 },
  "pure-E": { R: 1.5, I: 1.5, A: 1.5, S: 1.5, E: 6.5, C: 1.5 },
  "pure-C": { R: 1.5, I: 1.5, A: 1.5, S: 1.5, E: 1.5, C: 6.5 },
};

// v2.4: B2 value-counts per sentinel (для combined mode).
const SENTINEL_VALUES: Record<Exclude<Profile, null>, Record<string, number>> = {
  aladdin: { leadership: 5, growth: 4, prestige: 3, impact: 2, income: 2 },
  esmiralda: { meaning: 5, social: 4, growth: 3, impact: 2 },
  "all-I": { growth: 5, autonomy: 4, meaning: 2 },
  "all-R": { stability: 5, structure: 4, income: 3 },
  flat: { growth: 2, meaning: 2, variety: 2, stability: 2 },
  "pure-R": { stability: 4, structure: 3, income: 2 },
  "pure-I": { growth: 4, autonomy: 3, meaning: 2 },
  "pure-A": { creativity: 4, autonomy: 3, meaning: 2 },
  "pure-S": { meaning: 5, social: 4, impact: 3 },
  "pure-E": { leadership: 4, prestige: 3, income: 3, growth: 2 },
  "pure-C": { stability: 4, structure: 4, income: 3 },
};

// v2.4 mock-fix per методолог: B4 topSkills per profile (top-2).
// В проде score1822 deriv из B4 question answers. Mock — фикс-list под профиль
// для адекватного display values/skills-секций.
const SENTINEL_SKILLS: Record<Exclude<Profile, null>, string[]> = {
  aladdin: ["leadership", "communication"],   // ECI
  esmiralda: ["communication", "leadership"], // SEI
  "all-I": ["analytical", "technical"],
  "all-R": ["physical", "technical"],
  flat: ["communication", "analytical"], // not used (UnreliableProfileReport)
  "pure-R": ["physical", "technical"],
  "pure-I": ["analytical", "technical"],
  "pure-A": ["creative", "communication"],
  "pure-S": ["communication", "leadership"],
  "pure-E": ["leadership", "communication"],
  "pure-C": ["analytical", "technical"],
};

function buildMockResult(
  seed: number,
  lang: Lang1822,
  profile: Profile,
  mode: Mode,
): Result1822 {
  const rand = rng(seed);

  // RIASEC scores — sentinel или random seeded
  let riasecScores: Record<RiasecCode1822, number>;
  let riasecShuffled: RiasecCode1822[];
  if (profile && SENTINELS[profile]) {
    riasecScores = { ...SENTINELS[profile] };
    riasecShuffled = (Object.entries(riasecScores) as [RiasecCode1822, number][])
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k);
  } else {
    riasecShuffled = shuffle(RIASEC_CODES, rand);
    riasecScores = {} as Record<RiasecCode1822, number>;
    riasecShuffled.forEach((c, i) => {
      if (i === 0) riasecScores[c] = 4.5 + rand() * 1.5;
      else if (i === 1) riasecScores[c] = 3.5 + rand() * 1.0;
      else if (i === 2) riasecScores[c] = 2.5 + rand() * 1.0;
      else riasecScores[c] = 0.5 + rand() * 1.5;
    });
  }
  const riasecCode = riasecShuffled.slice(0, 3).join("");

  // v2.4 mock-fix: для sentinel-profile derive topValues из SENTINEL_VALUES
  // (sort desc, top-3) — РОВНО как PROD score1822 делает с реальным B2.
  // topSkills из SENTINEL_SKILLS под профиль. Для random — старая random-shuffle.
  let topValues: string[];
  let topSkills: string[];
  if (profile && SENTINEL_VALUES[profile]) {
    topValues = Object.entries(SENTINEL_VALUES[profile])
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([k]) => k);
  } else {
    topValues = shuffle(VALUE_KEYS, rand).slice(0, 3);
  }
  if (profile && SENTINEL_SKILLS[profile]) {
    topSkills = SENTINEL_SKILLS[profile];
  } else {
    topSkills = shuffle(SKILL_KEYS, rand).slice(0, 2);
  }
  const topStyles = shuffle(STYLE_KEYS, rand).slice(0, 3);

  // BUILD matrix-style mock placeholder first (needed for topValues passthrough)
  const matrixMock: Result1822 = {
    riasecScores,
    riasecCode,
    topValues,
    topStyles,
    topSkills,
    topSkill: topSkills[0] ?? null,
    depthTag: DEPTH_KEYS[Math.floor(rand() * DEPTH_KEYS.length)],
    context: {
      status: STATUS_KEYS[Math.floor(rand() * STATUS_KEYS.length)],
      experience: "1-3y",
      readiness: rand() > 0.5 ? "deepen" : "ready_change",
      education: "bachelor",
    },
    topClusters: [],
    confidence: "medium",
  };

  // v2.4: combined mode → передаём valueCounts (если sentinel), interest-only → нет.
  // Для random profile generate mock valueCounts из topValues (по 3 шт).
  let valueCounts: Record<string, number> | undefined;
  if (mode === "combined") {
    if (profile && SENTINEL_VALUES[profile]) {
      valueCounts = SENTINEL_VALUES[profile];
    } else {
      // Random — distribute weights к topValues
      valueCounts = {};
      topValues.forEach((v, i) => {
        valueCounts![v] = 3 - i;
      });
    }
  }
  return enrichWithPearson(matrixMock, lang, valueCounts);
}

function PreviewPearsonPage() {
  const { seed, name, age, lang, profile, mode } = Route.useSearch();
  const result = buildMockResult(seed, lang, profile, mode);
  const date = new Date();

  return (
    <div style={{ padding: 24, background: "#E7E2D6", minHeight: "100vh" }}>
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          marginBottom: 20,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <span
          style={{
            background: mode === "combined" ? "#1E5A6B" : "#6B8090",
            color: "#fff",
            padding: "8px 16px",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: ".04em",
          }}
        >
          🔬 PEARSON · {mode === "combined" ? "v2.4 combined" : "v2.2 interest-only"}
        </span>
        <Link
          to="/report/18-plus/preview-pearson"
          search={{ seed, name, age, lang, profile, mode: mode === "combined" ? "interest-only" : "combined" }}
          style={{
            background: "#F0A63A",
            color: "#0F2A38",
            padding: "8px 16px",
            borderRadius: 6,
            textDecoration: "none",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          ⇄ toggle: {mode === "combined" ? "→ interest-only" : "→ combined"}
        </Link>
        <Link
          to="/report/18-plus/preview"
          search={{ seed, name, age, lang }}
          style={{
            background: "#F0A63A",
            color: "#0F2A38",
            padding: "8px 16px",
            borderRadius: 6,
            textDecoration: "none",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          ⇆ A/B: /preview (matrix)
        </Link>
        <Link
          to="/report/18-plus/preview-pearson"
          search={{ seed: seed + 1, name, age, lang, profile: null, mode }}
          style={{
            background: "#1E5A6B",
            color: "#fff",
            padding: "8px 16px",
            borderRadius: 6,
            textDecoration: "none",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          ↻ Новый seed
        </Link>
        {(["ru", "az", "en"] as Lang1822[]).map((l) => (
          <Link
            key={l}
            to="/report/18-plus/preview-pearson"
            search={{ seed, name, age, lang: l, profile, mode }}
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              background: lang === l ? "#1E5A6B" : "#fff",
              color: lang === l ? "#fff" : "#1E5A6B",
              textDecoration: "none",
              border: "1px solid #1E5A6B",
              fontSize: 13,
            }}
          >
            {l.toUpperCase()}
          </Link>
        ))}
      </div>

      {/* Sentinel profile picker */}
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto 16px",
          padding: 12,
          background: "#fff",
          borderRadius: 6,
          border: "1px solid #1E5A6B33",
          fontSize: 12,
        }}
      >
        <span style={{ fontWeight: 700, marginRight: 8, color: "#6B8090" }}>
          Sentinel profile:
        </span>
        {(
          [
            null,
            "aladdin",
            "esmiralda",
            "all-I",
            "all-R",
            "flat",
            "pure-R",
            "pure-I",
            "pure-A",
            "pure-S",
            "pure-E",
            "pure-C",
          ] as Profile[]
        ).map((p) => (
          <Link
            key={p ?? "random"}
            to="/report/18-plus/preview-pearson"
            search={{ seed, name, age, lang, profile: p, mode }}
            style={{
              display: "inline-block",
              margin: "0 4px 4px 0",
              padding: "4px 8px",
              borderRadius: 4,
              background: profile === p ? "#1E5A6B" : "#F4F1EA",
              color: profile === p ? "#fff" : "#1E5A6B",
              textDecoration: "none",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            {p ?? "random"}
          </Link>
        ))}
      </div>

      {/* Diagnostic strip */}
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto 12px",
          padding: 10,
          background: "#FFF7E6",
          borderRadius: 6,
          fontSize: 12,
          color: "#7A5300",
        }}
      >
        <strong>Diagnostic</strong> · seed={seed} ·
        profile={profile ?? "random"} · mode={mode} · RIASEC={result.riasecCode} ·
        clusters=[{result.topClusters.map((c) => `${c.id}(${c.score.toFixed(3)})`).join(", ")}] ·
        confidence={result.confidence}
      </div>

      <div
        style={{
          background: "#fff",
          boxShadow: "0 24px 48px -24px rgba(15,42,56,.25)",
          maxWidth: 900,
          margin: "0 auto",
        }}
      >
        <Report13Page name={name} age={age} result={result} date={date} lang={lang} />
      </div>
    </div>
  );
}
